"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/components/auth/Auth";
import type { ApiWaveCuration } from "@/generated/models/ApiWaveCuration";
import type { ApiWaveCurationRequest } from "@/generated/models/ApiWaveCurationRequest";
import { commonApiPost } from "@/services/api/common-api";
import { getWaveCurationsQueryKey, sortWaveCurations } from "./useWaveCurations";
type WaveCurationMoveDirection = "previous" | "next";
interface MoveWaveCurationVariables {
  readonly curation: ApiWaveCuration;
  readonly targetPriorityOrder: number;
}
interface MoveWaveCurationContext {
  readonly previousCurations?: ApiWaveCuration[] | undefined;
}
interface MoveCurationParams {
  readonly curation: ApiWaveCuration;
  readonly direction: WaveCurationMoveDirection;
  readonly curations: readonly ApiWaveCuration[];
}
interface ReorderCurationParams {
  readonly curation: ApiWaveCuration;
  readonly targetPriorityOrder: number;
  readonly curations: readonly ApiWaveCuration[];
}
const getMovedCurations = ({
  curations,
  curationId,
  targetPriorityOrder
}: {
  readonly curations: readonly ApiWaveCuration[];
  readonly curationId: string;
  readonly targetPriorityOrder: number;
}): ApiWaveCuration[] => {
  const orderedCurations = sortWaveCurations(curations);
  const currentIndex = orderedCurations.findIndex(curation => curation.id === curationId);
  if (currentIndex < 0) {
    return orderedCurations;
  }
  const targetIndex = Math.max(0, Math.min(targetPriorityOrder - 1, orderedCurations.length - 1));
  if (targetIndex === currentIndex) {
    return orderedCurations;
  }
  const nextCurations = [...orderedCurations];
  const [movedCuration] = nextCurations.splice(currentIndex, 1);
  if (!movedCuration) {
    return orderedCurations;
  }
  nextCurations.splice(targetIndex, 0, movedCuration);
  return nextCurations.map((curation, index) => ({
    ...curation,
    priority_order: index + 1
  }));
};
const getErrorMessage = (error: unknown): string => {
  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "Failed to update curation order.";
};
export function useWaveCurationReorderMutation(t0) {
  const $ = _c(31);
  const {
    waveId
  } = t0;
  const queryClient = useQueryClient();
  const {
    requestAuth,
    setToast
  } = useAuth();
  let t1;
  if ($[0] !== waveId) {
    t1 = getWaveCurationsQueryKey(waveId);
    $[0] = waveId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryKey = t1;
  let t2;
  if ($[2] !== requestAuth || $[3] !== waveId) {
    t2 = async t3 => {
      const {
        curation,
        targetPriorityOrder
      } = t3;
      const auth = await requestAuth();
      if (!auth.success) {
        throw new Error("Authentication was cancelled.");
      }
      const body = {
        name: curation.name,
        group_id: curation.group_id,
        priority_order: targetPriorityOrder
      };
      return await commonApiPost({
        endpoint: `waves/${waveId}/curations/${curation.id}`,
        body,
        errorMode: "structured"
      });
    };
    $[2] = requestAuth;
    $[3] = waveId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== queryClient || $[6] !== queryKey) {
    t3 = async t4 => {
      const {
        curation: curation_0,
        targetPriorityOrder: targetPriorityOrder_0
      } = t4;
      await queryClient.cancelQueries({
        queryKey
      });
      const previousCurations = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, current => current ? getMovedCurations({
        curations: current,
        curationId: curation_0.id,
        targetPriorityOrder: targetPriorityOrder_0
      }) : current);
      return {
        previousCurations
      };
    };
    $[5] = queryClient;
    $[6] = queryKey;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  let t5;
  if ($[8] !== queryClient || $[9] !== queryKey || $[10] !== setToast) {
    t4 = savedCuration => {
      queryClient.setQueryData(queryKey, current_0 => {
        if (!current_0) {
          return [savedCuration];
        }
        return sortWaveCurations(current_0.map(curation_1 => curation_1.id === savedCuration.id ? savedCuration : curation_1));
      });
      setToast({
        type: "success",
        message: "Curation order updated."
      });
    };
    t5 = (error, _variables, context) => {
      if (context?.previousCurations !== undefined) {
        queryClient.setQueryData(queryKey, context.previousCurations);
      }
      setToast({
        type: "error",
        message: getErrorMessage(error)
      });
    };
    $[8] = queryClient;
    $[9] = queryKey;
    $[10] = setToast;
    $[11] = t4;
    $[12] = t5;
  } else {
    t4 = $[11];
    t5 = $[12];
  }
  let t6;
  if ($[13] !== queryClient || $[14] !== queryKey) {
    t6 = async () => {
      await queryClient.invalidateQueries({
        queryKey
      });
    };
    $[13] = queryClient;
    $[14] = queryKey;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t2 || $[17] !== t3 || $[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t7 = {
      mutationFn: t2,
      onMutate: t3,
      onSuccess: t4,
      onError: t5,
      onSettled: t6
    };
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const mutation = useMutation(t7);
  const {
    isPending,
    mutate,
    variables
  } = mutation;
  const pendingVariables = isPending ? variables : undefined;
  let t8;
  if ($[22] !== mutate) {
    t8 = t9 => {
      const {
        curation: curation_2,
        direction,
        curations
      } = t9;
      const orderedCurations = sortWaveCurations(curations);
      const currentIndex = orderedCurations.findIndex(item => item.id === curation_2.id);
      if (currentIndex < 0) {
        return;
      }
      const targetIndex = direction === "previous" ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= orderedCurations.length) {
        return;
      }
      mutate({
        curation: curation_2,
        targetPriorityOrder: targetIndex + 1
      });
    };
    $[22] = mutate;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const moveCuration = t8;
  let t9;
  if ($[24] !== mutate) {
    t9 = t10 => {
      const {
        curation: curation_3,
        targetPriorityOrder: targetPriorityOrder_1,
        curations: curations_0
      } = t10;
      const orderedCurations_0 = sortWaveCurations(curations_0);
      const currentIndex_0 = orderedCurations_0.findIndex(item_0 => item_0.id === curation_3.id);
      const targetIndex_0 = targetPriorityOrder_1 - 1;
      if (currentIndex_0 < 0 || targetIndex_0 < 0 || targetIndex_0 >= orderedCurations_0.length || targetIndex_0 === currentIndex_0) {
        return;
      }
      mutate({
        curation: curation_3,
        targetPriorityOrder: targetPriorityOrder_1
      });
    };
    $[24] = mutate;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const reorderCuration = t9;
  const t10 = pendingVariables?.curation.id ?? null;
  let t11;
  if ($[26] !== isPending || $[27] !== moveCuration || $[28] !== reorderCuration || $[29] !== t10) {
    t11 = {
      moveCuration,
      reorderCuration,
      isPending,
      pendingCurationId: t10
    };
    $[26] = isPending;
    $[27] = moveCuration;
    $[28] = reorderCuration;
    $[29] = t10;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  return t11;
}